import React, { useEffect, useState } from 'react';
import { Drawer, Empty, Tabs, Typography } from 'antd';
import api from '../../api';
import { EngagementTable } from '../../pages/MyEngagementsPage';
import EngagementLeaderboardPage from '../../pages/EngagementLeaderboardPage';

/**
 * The engagements this team is taking part in.
 *
 * My Engagements answers "what am I in". Nothing answered "what is my team
 * in", which is the question a supervisor actually has, so this is that view:
 * the same split and the same table, scoped to the team rather than the
 * person.
 *
 * Team-keyed, not campaign-keyed. Engagements are run against teams, so this
 * tab follows the team selector and ignores the campaign one.
 */
export default function EngagementsTab({ teamId, team }) {
  // Opened in a panel rather than navigated to. The campaign, team and window
  // above are page state, not URL state, so leaving and coming back would mean
  // choosing them again and pressing Run -- which is what made the back button
  // on that page wrong here rather than merely mislabelled.
  const [openId, setOpenId] = useState(null);

  const [tabKey,     setTabKey]     = useState('active');
  const [active,     setActive]     = useState([]);
  const [prior,      setPrior]      = useState([]);
  const [loadActive, setLoadActive] = useState(false);
  const [loadPrior,  setLoadPrior]  = useState(false);
  const [priorLoaded, setPriorLoaded] = useState(false);

  const load = (tab, setRows, setLoading) => {
    setLoading(true);
    api.get('/api/campaign-summary/engagements', { params: { team_id: teamId, tab } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  // Prior is fetched on demand, and both are reset when the team changes so a
  // stale list cannot sit under a new team's heading.
  useEffect(() => {
    setPrior([]); setPriorLoaded(false); setTabKey('active');
    if (!teamId) { setActive([]); return; }
    load('active', setActive, setLoadActive);
  }, [teamId]); // eslint-disable-line

  if (!teamId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Choose a team — engagements are run against a team, not a campaign"
        style={{ marginTop: 40 }}
      />
    );
  }

  const onTabChange = (key) => {
    setTabKey(key);
    if (key === 'prior' && !priorLoaded) {
      setPriorLoaded(true);
      load('prior', setPrior, setLoadPrior);
    }
  };

  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        Engagements {team?.Name ? <strong>{team.Name}</strong> : 'this team'} is taking part in.
        Active includes anything completed in the last 30 days, so a contest that has just
        finished is still here while people are talking about it.
      </Typography.Paragraph>

      <Tabs
        activeKey={tabKey}
        onChange={onTabChange}
        items={[
          {
            key: 'active',
            label: 'Active Engagements',
            children: <EngagementTable rows={active} loading={loadActive} onOpen={setOpenId} />,
          },
          {
            key: 'prior',
            label: 'Prior Engagements',
            children: <EngagementTable rows={prior} loading={loadPrior} onOpen={setOpenId} />,
          },
        ]}
      />

      <Drawer
        open={!!openId}
        onClose={() => setOpenId(null)}
        width="min(1100px, 95vw)"
        destroyOnClose
        styles={{ body: { paddingTop: 12 } }}
      >
        {openId && <EngagementLeaderboardPage engagementId={openId} inPanel />}
      </Drawer>
    </>
  );
}
